var Collections = require("./src/collections");
var objectHash = require("./src/hash.js").objectHash;

var DescriptionRoot = require("./src/datamodel/descriptionRoot.js");
var JsonParser = require("./src/datamodel/jsonParser.js");

/**
 * ContentServerAssetLoader is a helper to retreive, unpack and cache data from the content-server
 * It works for two general types of data: descriptions and models
 * Descriptions are used for all cio-generated data.
 * Models are only used for 3d model files.
 */
var ContentServerAssetLoader = function (dataManager, config) {
    this.dataManager = dataManager;
    this.datamodel_version = config.datamodel_version;
    console.assert(config.datamodel_version !== undefined, "Datamodel version undefined");
    this.model_map = new Collections.HashMap(); //for loaded 3d models
    this.description_map = new Collections.HashMap(); //for loaded descriptions
    this.descriptionPromise_map = new Collections.HashMap(); //for loaded and in-flight loading descriptions
    this.snapShotMap = new Collections.HashMap();
    this.snapShotPromise_map = new Collections.HashMap();

    this.batchLoadJobs = [];

    // populate assetLoader with initial assets
    for (var i in config.initialAssets){
        this.loadDescription(config.initialAssets[i]);
    }
};

module.exports = ContentServerAssetLoader;

ContentServerAssetLoader.prototype.loadDescription = function (jsonData, async, skipCache, nodesToSkip) {
    var self = this;
    function part2() {
        console.assert(jsonData["header"]["datamodel_version"] === self.datamodel_version, "Datamodel version mismatch");
        if(jsonData["body"]['$id'] === undefined) throw new Error("Empty data received from contentserver");
    }
    function part3() {
        // TODO write a migration for this!!
        console.assert(jsonData["header"], "No header in json");
        console.assert(jsonData["header"]["assetType"], "Document has no asset type");
        console.assert(jsonData["header"]["assetId"], "Document has no asset id");
        console.assert(jsonData["header"]["version"], "Document has no version number");
        var type = jsonData["header"]["assetType"];
        var id = jsonData["header"]["assetId"].toString();
        var version = jsonData["header"]["version"].toString();
        console.assert(type && id && version, "Header of snapshot should contain at least the assetType, assetId and version");

        var descriptionRoot = new DescriptionRoot(self, jsonData, nodesToSkip);

        var key = self.generateDescriptionKey(type, id, version);
        console.assert(!self.description_map.contains(key), "Unexpected");
        if(!skipCache) {
            self.description_map.put(key, descriptionRoot.data);
            //put the loaded description in the map
            self.descriptionPromise_map.put(key, Promise.resolve(descriptionRoot.data)); //even if the description was not asynchronously loaded, it's useful to have a promise in the map for future async calls
        }
        return descriptionRoot.data;
    }
    if(async) {
        return Promise.resolve()
        .then(part2)
        .then(function() {
            return self.preloadReferencedAssets(jsonData.body, skipCache, nodesToSkip);
        })
        .then(part3);
    } else {
        part2();
        return part3();
    }
};

ContentServerAssetLoader.prototype.getSnapShot = function (type, id, version, async) {
    let self = this;
    var key = self.generateDescriptionKey(type, id, version);

    if(async) {
        if(!self.snapShotPromise_map.contains(key))
            self.snapShotPromise_map.put(key, self.dataManager.getResourceSnapshot(type, id, version, self.datamodel_version, false));

        return self.snapShotPromise_map.get(key)
            .then(function(snapshot) {
                if(!self.snapShotMap.contains(key)) self.snapShotMap.put(key, snapshot);
                return snapshot;
            })
    }else {
        if(self.snapShotMap.contains(key))
            return self.snapShotMap.get(key);

        var snapshot = self.dataManager.getResourceSnapshot(type, id, version, self.datamodel_version, true);
        self.snapShotMap.put(key, snapshot);
        self.snapShotPromise_map.put(key, Promise.resolve(snapshot));
        return self.snapShotMap.get(key);
    }
}

ContentServerAssetLoader.prototype.getDescription = function (type, id, version, async, skipCache, nodesToSkip) {
    console.assert(id, "Asset id undefined");
    console.assert(version, "Asset version undefined");
    var self = this;
    var key = self.generateDescriptionKey(type, id, version);

    if(async) {
        if(self.descriptionPromise_map.contains(key)) {//check the promise map
            //console.log("Getting "+type+" "+id+" v"+version+" from cache");
            return self.descriptionPromise_map.get(key);
        } else {
            //console.log("Loading "+type+" "+id+" v"+version+" from server");
            var promise = this.getSnapShot(type, id, version, async)//part1()
                .then(function(jsonData){
                    return self.loadDescription(jsonData, async, skipCache, nodesToSkip);
                });
            if(!skipCache) self.descriptionPromise_map.put(key, promise);
            return promise;
        }

    } else {
        if(this.description_map.contains(key)) {//check the description map
            //console.log("Getting "+type+" "+id+" v"+version+" from cache");
            return this.description_map.get(key);
        } else {
            //console.log("Loading "+type+" "+id+" v"+version+" from server");
            return self.loadDescription(this.getSnapShot(type, id, version, async), async, skipCache, nodesToSkip);
        }
    }
};

ContentServerAssetLoader.prototype.getDescriptionRoot = function(id){
    if (!this.description_map.contains(this.generateDescriptionKey(id)))
        return this.getDescription(id["type"], id["id"], id["version"], false, true).root;
    return this.description_map.get(this.generateDescriptionKey(id)).root;
};

ContentServerAssetLoader.prototype.generateDescriptionKey = function (type, id, version) {
    if (type.constructor == Object) return type["type"] + "_" + type["id"] + "_v" + type["version"];
    else return type + "_" + id + "_v" + version;
};

ContentServerAssetLoader.prototype.removeDescription = function(type, id, version) {
    let key = this.generateDescriptionKey(type, id, version);

    this.descriptionPromise_map.remove(key);
    this.description_map.remove(key);
}

ContentServerAssetLoader.prototype.generateSnapShotKey = function (snapshot) {
    var assetType = snapshot['header']['assetType'];
    if(assetType.endsWith('s')) assetType = assetType.slice(0, assetType.length-1);
    var key = this.generateDescriptionKey(assetType, snapshot['header']['assetId'], snapshot['header']['version']);

    return key;
}

ContentServerAssetLoader.prototype.getModel = function (type, id, ignoreErrors, sync) {
    console.assert(type, "Asset type undefined");
    console.assert(id, "Asset id undefined");
    var self = this;

    var data = this.model_map.get(self.generateModelKey(id) + type);
    if(data != undefined){
        console.log("Getting model "+id+" from cache");
        if(sync){
            return data;
        } else {
            return new Promise(function(resolve, reject){
                resolve(data);
            })
        }
    } 
  
    console.log("Loading model "+id+" from server");
    if(sync){
        data = this.dataManager.getResourceModel(type, id, ignoreErrors, true);
        console.assert(data != undefined, "no data returned");
        this.model_map.put(self.generateModelKey(id) + type, data);
        return data;
     } else {
        return this.dataManager.getResourceModel(type, id, ignoreErrors)
        .then( data => {
            console.assert(data != undefined, "no data returned");
            this.model_map.put(self.generateModelKey(id) + type, data);
            return data;
        })
    }
   
};


ContentServerAssetLoader.prototype.convertModel = function (type, id, sync) {
    console.assert(type, "Asset type undefined");
    console.assert(id, "Asset id undefined");

    return this.dataManager.convertResourceModel(type, id, sync);
}

ContentServerAssetLoader.prototype.getModelStatus = function (type, id, sync) {
    console.assert(type, "Asset type undefined");
    console.assert(id, "Asset id undefined");

    return this.dataManager.checkModelStatus(type, id, sync);
}

ContentServerAssetLoader.prototype.checkModelExists = function (type, id, sync) {
    console.assert(type, "Asset type undefined");
    console.assert(id, "Asset id undefined");

    return this.dataManager.checkModelExists(type, id, sync);
}

ContentServerAssetLoader.prototype.generateModelKey = function (id) {
    return id;
};

ContentServerAssetLoader.prototype.preloadReferencedAssets = function (json, skipDescriptions, nodesToSkip) {
    let invClassMap = JsonParser.getClassMap();
    if(!nodesToSkip) nodesToSkip = [];
    var referencesMap = new Collections.HashSet(objectHash);
    var collect_references_rec = function(obj, skipRefs) {
        if(obj && obj["$class"]) {
            var Constructor = invClassMap.get(obj["$class"]);
            if(!Constructor) throw new Error("Cannot preload unregistered class " + obj["$class"]);
            var data = new Constructor();
        }

        if (obj && obj["$asset"]) {
            var assetRef = obj["$asset"];

            if(!skipRefs) {
                referencesMap.add({type: assetRef["type"], id: assetRef["id"], version: assetRef["version"]});
            }
        }
        if(typeof obj === 'object') {
            for(var i in obj) {
                if(!skipRefs) {
                    if (data && data.collections) var collection = data.collections.get(i);
                    if (collection && nodesToSkip.indexOf(collection._name) != -1) skipRefs = true;
                }

                collect_references_rec(obj[i], skipRefs);
            }
        }
    };
    collect_references_rec(json);
    var references = referencesMap.values();

    return this.batchLoadReferences(references, skipDescriptions, nodesToSkip);
};

ContentServerAssetLoader.prototype.addToSnapShotMap = function(collabDoc) {
    let key = this.generateDescriptionKey(collabDoc["header"]["assetType"], collabDoc["header"]["assetId"], collabDoc["header"]["version"]);

    this.snapShotPromise_map.put(key, Promise.resolve(collabDoc));
    this.snapShotMap.put(key, collabDoc);
};

//If we skip descriptions we will just return snapshots instead.
ContentServerAssetLoader.prototype.batchLoadReferences = function(references, skipDescriptions, nodesToSkip) {
    var self = this;
    //create a promise that resolves when all references are loaded (including those that were already being loaded/in the promisemap)
    var all_promises = references.map(function(reference) {
        return self.batchGetSnapShot(reference.type, reference.id, reference.version)
            .then(function(snapShot) {
                if (skipDescriptions) {
                    return self.preloadReferencedAssets(snapShot.body, skipDescriptions)
                        .then(() => {
                            return snapShot;
                        });
                } else {
                    return self.getDescription(reference.type, reference.id, reference.version, /*async*/true, /*skipCache*/false, nodesToSkip)
                        .then((description) => {
                            return description;
                        });
                }
            })
    });
    return Promise.all(all_promises)
        .then(() => {
            return;
        })
};

ContentServerAssetLoader.prototype.batchGetSnapShot = function(type, id, version) {
    console.assert(type, "Asset type undefined");
    console.assert(id, "Asset id undefined");
    console.assert(version, "Asset version undefined");
    var self = this;
    var key = self.generateDescriptionKey(type, id, version);

    if(self.snapShotPromise_map.contains(key)) {//check the promise map
        //console.log("Getting "+type+" "+id+" v"+version+" from cache");
        return self.snapShotPromise_map.get(key);
    } else {
        // console.log("Scheduling batch loading "+type+" "+id+" v"+version+" from server");
        var promise = self.scheduleBatchLoadJob(type, id, version);
        self.snapShotPromise_map.put(key, promise);
        return promise;
    }
};

ContentServerAssetLoader.prototype.scheduleBatchLoadJob = function(type, id, version) {
    var self = this;
    var promise = new Promise(function(resolve, reject) {
        self.batchLoadJobs.push({
            type: type,
            id: id,
            version: version,
            resolve: resolve,
            reject: reject
        });
    });
    this.tickBatchLoadJobs();
    return promise;
};

var batchMinSize = 25;
var batchTimout_ms = 100;
ContentServerAssetLoader.prototype.tickBatchLoadJobs = function() {
    var self = this;
    if(this.batchLoadJobs.length > batchMinSize) {
        //if there's enough work, run now
        if(this.batchTimeout) {//clear any existing timeouts
            clearTimeout(this.batchTimeout);
            this.batchTimeout = null;
        }
        this.runBatchLoadSnapShots();
    } else if (this.batchLoadJobs.length > 0 && !this.batchTimeout) {
        //otherwise wait a little longer, but guarantee loading using a timeout
        this.batchTimeout = setTimeout(function(){
            self.batchTimeout = null;
            self.runBatchLoadSnapShots();
        }, batchTimout_ms);
    } else {
        ;//a timeout is already set so we just have to wait
        //or there is nothing to do
    }
};

ContentServerAssetLoader.prototype.runBatchLoadSnapShots = function() {
    var self = this;
    //select a batch
    var batch = this.batchLoadJobs.splice(0, batchMinSize);

    // console.log("Running batch load with size: "+batch.length);
    //download the batch
    this.dataManager.getMultipleResourceSnapshots(batch, self.datamodel_version, false)
    .then(function(snapshots) {
        self.tickBatchLoadJobs(); //we can already start the next batch
        snapshots.forEach(function(snapshot) {
            var key = self.generateSnapShotKey(snapshot);
            self.snapShotMap.put(key, snapshot);
        });

        return Promise.all(batch.map(function(job) {
            var assetType = job.type;
            if(assetType.endsWith('s')) assetType = assetType.slice(0, assetType.length-1);
            var key = self.generateDescriptionKey(assetType, job.id, job.version);
            if(!self.snapShotMap.contains(key)) {
                job.reject(new Error("Reference was not loaded as expected: " + key));
            } else {//resolve and load description
                job.resolve(self.snapShotMap.get(key));
            }
        }));
    })
    .catch(function(err) {
        batch.forEach(function(ref) {
            ref.reject(err);
        });
    });
};
